import { publishEvent } from 'serviceWorker/broadcasts/publishers';
import { dataFlushContract } from 'shared/domain/dataFlush/dataFlushContract';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { LogLevel } from 'shared/types/logger';
import { swSignals } from 'serviceWorker/events/swSignals';
import { findCollectionsOfAnotherProject, flushData, flushProjectData } from 'serviceWorker/db/flush';
import { awaitInFlightWrites } from 'serviceWorker/helpers/dataScope';
import { logLeftoverDataOfAnotherProject, logWritesStillRunningAtFlush } from 'serviceWorker/helpers/dataLog';
import { requestSuspender } from 'serviceWorker/api/RequestSuspender';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';

/** How long a flush waits for the writes running when it was requested. */
const IN_FLIGHT_WRITES_TIMEOUT_MS = 5000;

/**
 * Runs a flush with requests to the server held back; they go on even when it
 * fails. The writes already running finish first (the signal that asked for
 * the flush outdated all others), so none lands after it.
 */
async function flushWithRequestsSuspended(flush: () => Promise<void>): Promise<void> {
  requestSuspender.suspend();
  try {
    if (!(await awaitInFlightWrites(IN_FLIGHT_WRITES_TIMEOUT_MS))) {
      void logWritesStillRunningAtFlush(IN_FLIGHT_WRITES_TIMEOUT_MS);
    }
    await flush();
  } finally {
    requestSuspender.resume();
  }
}

async function clearData(): Promise<void> {
  try {
    await flushWithRequestsSuspended(flushData);
  } catch (error) {
    swLog('SW could not flush the data', LogLevel.ERROR, error, null);
    return;
  }
  // Tells the pages (permissions changed): they refresh the session and reload their data.
  publishEvent(dataFlushContract, DomainMessagesTypes.dataFlushed, undefined);
}

/**
 * After the project data is flushed no collection may belong to another
 * project; otherwise the guard of the stored data failed (error log with the
 * state of the service tables). Never rejects.
 */
async function verifyNoCollectionsOfAnotherProject(): Promise<void> {
  try {
    const { projectId, tables } = await findCollectionsOfAnotherProject();
    if (projectId && tables.length) {
      await logLeftoverDataOfAnotherProject('the project data flush', tables, projectId);
    }
  } catch (error) {
    swLog('SW could not check the collections after the project data flush', LogLevel.INFO, error, null);
  }
}

/**
 * Flushes the data of the selected project with requests held back, then
 * tells the pages. Rejects when the flush fails; requests go on.
 */
export async function clearProjectData(): Promise<void> {
  await flushWithRequestsSuspended(flushProjectData);
  void verifyNoCollectionsOfAnotherProject();
  // Tells the pages (needed for language change, etc.).
  publishEvent(dataFlushContract, DomainMessagesTypes.projectDataFlushed, undefined);
}

async function clearProjectDataInOtherLanguage(): Promise<void> {
  try {
    await clearProjectData();
  } catch (error) {
    swLog('SW could not flush the project data', LogLevel.ERROR, error, null);
  }
}

/**
 * A project switch flushes through `clearProjectData` and waits for it;
 * `projectDataReplaced` only aborts the running operations.
 */
export const init = (): void => {
  swSignals.permissionsChanged.subscribe(clearData);
  swSignals.languageChanged.subscribe(clearProjectDataInOtherLanguage);
};
